import { useState } from "react";
import { BAR_COLOR, BarTrack, MIN_BAR_SEGMENT_WIDTH } from "@/components/billing/BarTrack";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DialogFrame } from "@/components/common/DialogFrame";
import { ResourceStatus } from "@/components/common/ResourceCard";
import { TextInputField } from "@/components/common/TextInputField";
import {
	updateProjectCreditLimit,
	type ProjectCreditLimit,
	type ProjectCreditLimitPayload,
	type ProjectCreditLimitPeriod,
	type ProjectCreditPeriodLimit,
} from "@/lib/api";
import {
	formatCredits,
	parseCreditLimitInput,
	periodsStoppedBySave,
	projectCreditPeriodLimitStatus,
	type ProjectCreditLimitStatus,
} from "@/lib/billing";

const PERIODS: ProjectCreditLimitPeriod[] = ["daily", "monthly"];

const USED_LABELS: Record<ProjectCreditLimitPeriod, string> = {
	daily: "今日の消費",
	monthly: "今月の消費",
};

const PERIOD_HEADINGS: Record<ProjectCreditLimitPeriod, string> = {
	daily: "日ごとの上限 (日本時間の 0 時に区切る)",
	monthly: "月ごとの上限 (日本時間の 1 日 0 時に区切る)",
};

/** 上限の値をバーのすぐ右に置くため、バーは伸ばさず固定幅にする。 */
const LIMIT_BAR_WIDTH = 160;

const STATUS_COLORS: Record<ProjectCreditLimitStatus, string> = {
	none: BAR_COLOR,
	softReached: "var(--warning)",
	hardReached: "var(--danger)",
};

/**
 * プロジェクトのカードに添えるクレジット上限。日・月の集計期間ごとに消費・バー・ソフト・ハードの上限を
 * 1 行に並べる。バーは大きい方の上限を全幅とし、両方あればソフト・リミットの位置に目盛りを引く。
 * 値は課金ページの期間・カテゴリの絞り込みに関係なく、現在の集計期間のもの。
 */
export function ProjectCreditLimitSummary({
	limit,
	onSaved,
}: {
	limit: ProjectCreditLimit;
	onSaved: () => void;
}) {
	const [editing, setEditing] = useState(false);

	return (
		<div data-testid={`project-limit-${limit.projectId}`} style={{ display: "contents" }}>
			<div
				style={{
					gridColumn: "1 / -1",
					display: "grid",
					gridTemplateColumns: `max-content max-content ${LIMIT_BAR_WIDTH}px max-content max-content`,
					justifyContent: "start",
					alignItems: "center",
					columnGap: 12,
					rowGap: 6,
					// 行間 6px に足して、内訳との間を空けて区切り線を引く。
					marginTop: 6,
					paddingTop: 10,
					borderTop: "var(--pane-header-border)",
					fontSize: "var(--text-caption)",
					fontVariantNumeric: "tabular-nums",
					whiteSpace: "nowrap",
				}}
			>
				<div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8 }}>
					<span style={{ fontWeight: 600, color: "var(--fg-2)" }}>クレジット上限</span>
					<ConfirmButton
						label="設定"
						loading={false}
						size="sm"
						variant="secondary"
						onClick={() => setEditing(true)}
						data-testid={`project-limit-edit-${limit.projectId}`}
					/>
				</div>
				{PERIODS.map((period) => (
					<PeriodLimitRow key={period} period={period} limit={limit[period]} />
				))}
			</div>
			{/* DialogFrame は親の文字の指定を継ぐため、行の外に置く。 */}
			{editing && (
				<ProjectCreditLimitForm
					limit={limit}
					onClose={() => setEditing(false)}
					onSaved={() => {
						setEditing(false);
						onSaved();
					}}
				/>
			)}
		</div>
	);
}

function PeriodLimitRow({
	period,
	limit,
}: {
	period: ProjectCreditLimitPeriod;
	limit: ProjectCreditPeriodLimit;
}) {
	const status = projectCreditPeriodLimitStatus(limit);
	const { softLimitCredits: soft, hardLimitCredits: hard, usedCredits: used } = limit;
	const scale = Math.max(soft ?? 0, hard ?? 0);
	const limitValue = (label: string, value: number | null, reached: boolean) => (
		<span>
			<span style={{ color: "var(--fg-3)", marginRight: 6 }}>{label}</span>
			<span
				style={{
					color: value === null ? "var(--fg-4)" : reached ? STATUS_COLORS[status] : "var(--fg-2)",
				}}
			>
				{value === null ? "未設定" : formatCredits(value)}
			</span>
		</span>
	);

	return (
		<>
			<span style={{ paddingLeft: 12, color: "var(--fg-3)" }}>{USED_LABELS[period]}</span>
			<span style={{ textAlign: "right", color: "var(--fg-2)" }}>{formatCredits(used)}</span>
			{/* 上限が未設定でも行の形を揃えるため、下地のトラックだけは出す。 */}
			<div style={{ position: "relative" }}>
				<BarTrack>
					{scale > 0 && used > 0 && (
						<div
							style={{
								width: `${Math.min(used / scale, 1) * 100}%`,
								minWidth: MIN_BAR_SEGMENT_WIDTH,
								height: "100%",
								borderRadius: "var(--radius-full)",
								background: STATUS_COLORS[status],
							}}
						/>
					)}
				</BarTrack>
				{soft !== null && hard !== null && soft < hard && (
					<div
						style={{
							position: "absolute",
							top: -2,
							bottom: -2,
							left: `${(soft / scale) * 100}%`,
							width: 2,
							background: "var(--fg-3)",
						}}
					/>
				)}
			</div>
			{limitValue("ソフト・リミット", soft, status !== "none")}
			{limitValue("ハード・リミット", hard, status === "hardReached")}
		</>
	);
}

const BADGES: Record<
	Exclude<ProjectCreditLimitStatus, "none">,
	{ label: string; tone: "warning" | "danger" }
> = {
	softReached: { label: "ソフト・リミット到達", tone: "warning" },
	hardReached: { label: "ハード・リミット到達・停止中", tone: "danger" },
};

/** 上限への到達を示す状態表示。到達していなければ何も出さない。 */
export function ProjectCreditLimitBadge({ status }: { status: ProjectCreditLimitStatus }) {
	if (status === "none") return null;
	const { label, tone } = BADGES[status];
	return (
		<ResourceStatus tone={tone} testId={`project-limit-badge-${status}`}>
			{label}
		</ResourceStatus>
	);
}

type PeriodInput = { soft: string; hard: string };
type PeriodErrors = { soft: string | null; hard: string | null };

function toInput(limit: ProjectCreditPeriodLimit): PeriodInput {
	return {
		soft: limit.softLimitCredits?.toString() ?? "",
		hard: limit.hardLimitCredits?.toString() ?? "",
	};
}

function ProjectCreditLimitForm({
	limit,
	onClose,
	onSaved,
}: {
	limit: ProjectCreditLimit;
	onClose: () => void;
	onSaved: () => void;
}) {
	const [inputs, setInputs] = useState<Record<ProjectCreditLimitPeriod, PeriodInput>>({
		daily: toInput(limit.daily),
		monthly: toInput(limit.monthly),
	});
	const [errors, setErrors] = useState<Record<ProjectCreditLimitPeriod, PeriodErrors>>({
		daily: { soft: null, hard: null },
		monthly: { soft: null, hard: null },
	});
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	// 消費以下のハード・リミットは保存した途端に止まるため、確かめてから保存する。
	const [pendingStop, setPendingStop] = useState<{
		payload: ProjectCreditLimitPayload;
		periods: ProjectCreditLimitPeriod[];
	} | null>(null);

	async function save(payload: ProjectCreditLimitPayload) {
		await updateProjectCreditLimit(limit.projectId, payload);
		onSaved();
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		const parsed = Object.fromEntries(
			PERIODS.map((period) => {
				const soft = parseCreditLimitInput(inputs[period].soft);
				const hard = parseCreditLimitInput(inputs[period].hard);
				const orderError =
					soft.value !== null && hard.value !== null && soft.value > hard.value
						? "ソフト・リミットはハード・リミット以下にしてください。"
						: null;
				return [
					period,
					{ soft, hard, errors: { soft: soft.error ?? orderError, hard: hard.error } },
				];
			}),
		) as Record<
			ProjectCreditLimitPeriod,
			{
				soft: ReturnType<typeof parseCreditLimitInput>;
				hard: ReturnType<typeof parseCreditLimitInput>;
				errors: PeriodErrors;
			}
		>;
		setErrors({ daily: parsed.daily.errors, monthly: parsed.monthly.errors });
		if (PERIODS.some((period) => parsed[period].errors.soft || parsed[period].errors.hard)) return;

		const payload: ProjectCreditLimitPayload = {
			daily: {
				softLimitCredits: parsed.daily.soft.value,
				hardLimitCredits: parsed.daily.hard.value,
			},
			monthly: {
				softLimitCredits: parsed.monthly.soft.value,
				hardLimitCredits: parsed.monthly.hard.value,
			},
		};
		const stopped = periodsStoppedBySave(limit, {
			daily: payload.daily.hardLimitCredits,
			monthly: payload.monthly.hardLimitCredits,
		});
		if (stopped.length > 0) {
			setPendingStop({ payload, periods: stopped });
			return;
		}

		setSubmitError(null);
		setSaving(true);
		try {
			await save(payload);
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
			throw err;
		} finally {
			setSaving(false);
		}
	}

	return (
		<>
			<DialogFrame
				open
				title={`「${limit.projectName}」のクレジット上限`}
				width={480}
				onClose={() => {
					if (!saving) onClose();
				}}
				onSubmit={(e) => {
					handleSubmit(e).catch((err: unknown) => console.error(err));
				}}
				footer={
					<>
						<ConfirmButton
							variant="ghost"
							label="キャンセル"
							loading={false}
							disabled={saving}
							onClick={onClose}
						/>
						<ConfirmButton
							type="submit"
							label="保存"
							loading={saving}
							data-testid="project-limit-save"
						/>
					</>
				}
			>
				{submitError && (
					<div
						style={{
							padding: "10px 14px",
							borderRadius: "var(--radius-3)",
							background: "var(--danger-subtle)",
							boxShadow: "0 0 0 1px var(--danger-line)",
							fontSize: "var(--text-ui)",
							color: "var(--danger)",
						}}
					>
						{submitError}
					</div>
				)}
				<p
					style={{
						margin: 0,
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-normal)",
						color: "var(--fg-3)",
					}}
				>
					ソフト・リミットに達すると管理者全員にメールで知らせます。ハード・リミットに達すると、このプロジェクトの
					メッセージの送信・クラウドのエージェントの起動を止め、実行中の処理と稼働中のクラウドのエージェントを停止します。
					日・月の両方を設定でき、どちらかに達すると働きます。空欄にするとその上限は設定しません。
				</p>
				<div
					data-testid="project-limit-interruption-warning"
					style={{
						padding: "10px 14px",
						borderRadius: "var(--radius-3)",
						background: "var(--warning-subtle)",
						boxShadow: "0 0 0 1px var(--warning-line)",
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-normal)",
						color: "var(--warning)",
					}}
				>
					ハード・リミットに達すると、このプロジェクトで稼働中の GPU
					インスタンスと実行中の学習ジョブも途中で中断されます。
				</div>
				{PERIODS.map((period) => (
					<fieldset
						key={period}
						style={{ margin: 0, padding: 0, border: "none", display: "grid", gap: 8 }}
					>
						<legend
							style={{
								padding: 0,
								marginBottom: 8,
								fontSize: "var(--text-ui)",
								fontWeight: 600,
								color: "var(--fg-2)",
							}}
						>
							{PERIOD_HEADINGS[period]}
						</legend>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<TextInputField
								label="ソフト・リミット (クレジット)"
								value={inputs[period].soft}
								onChange={(soft) =>
									setInputs((prev) => ({ ...prev, [period]: { ...prev[period], soft } }))
								}
								placeholder="未設定"
								autoComplete="off"
								error={errors[period].soft ?? undefined}
							/>
							<TextInputField
								label="ハード・リミット (クレジット)"
								value={inputs[period].hard}
								onChange={(hard) =>
									setInputs((prev) => ({ ...prev, [period]: { ...prev[period], hard } }))
								}
								placeholder="未設定"
								autoComplete="off"
								error={errors[period].hard ?? undefined}
							/>
						</div>
					</fieldset>
				))}
				<p
					style={{
						margin: 0,
						fontSize: "var(--text-caption)",
						lineHeight: "var(--leading-normal)",
						color: "var(--fg-3)",
					}}
				>
					停止したプロジェクトは、上限を引き上げるか次の集計期間になると 1
					分ほどで再開できるようになります。
				</p>
			</DialogFrame>
			{/* 外側のダイアログの Escape に伝わって両方閉じないよう、入れ子にせず並べる。 */}
			{pendingStop && (
				<ConfirmDialog
					open
					title="プロジェクトの処理が停止します"
					message="ハード・リミットがすでに消費したクレジット以下です。保存すると 1 分ほどで、このプロジェクトのメッセージの送信・クラウドのエージェントの起動が止まり、実行中の処理と稼働中のクラウドのエージェントが停止します。稼働中の GPU インスタンスと実行中の学習ジョブも途中で中断されます。"
					confirmLabel="保存して停止"
					onConfirm={() => save(pendingStop.payload)}
					onCancel={() => setPendingStop(null)}
				>
					<ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: "var(--text-caption)" }}>
						{pendingStop.periods.map((period) => (
							<li key={period} style={{ color: "var(--fg-2)", fontVariantNumeric: "tabular-nums" }}>
								{USED_LABELS[period]} {formatCredits(limit[period].usedCredits)} / ハード・リミット{" "}
								{formatCredits(pendingStop.payload[period].hardLimitCredits ?? 0)}
							</li>
						))}
					</ul>
				</ConfirmDialog>
			)}
		</>
	);
}
